import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from './Spinner';
import { api, AuthError } from '@/lib/api';
import type { ArticleListItem } from '@shared/types';

const CATEGORY_LABELS: Record<string, string> = {
  'getting-started': 'Getting Started',
  marketplace: 'Marketplace',
  csa: 'CSA Shares',
  register: 'Register (POS)',
  wholesale: 'Wholesale',
  'orders-and-fulfillment': 'Orders & Fulfillment',
  'payments-and-billing': 'Payments & Billing',
  'for-shoppers': 'For Shoppers',
  customers: 'Customers',
  'account-and-settings': 'Account & Settings',
  'whats-new': "What's New",
  'contact-support': 'Contact Support',
};

interface Props {
  open: boolean;
  token: string;
  onClose: () => void;
  onSelectArticle: (filePath: string, initialContent: string, prTitle: string) => void;
  onAuthError: () => void;
}

export function ArticleSearchDialog({ open, token, onClose, onSelectArticle, onAuthError }: Props) {
  const [articles, setArticles] = useState<ArticleListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingContent, setLoadingContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    api.articles(token)
      .then((res) => setArticles(res.articles))
      .catch((e) => {
        if (e instanceof AuthError) { onAuthError(); return; }
        setError(e instanceof Error ? e.message : 'Failed to load articles');
      })
      .finally(() => setLoading(false));
  }, [open, token]);

  const filtered = articles.filter((a) => {
    const q = query.toLowerCase();
    const matchesText = !q || a.title.toLowerCase().includes(q) || a.filePath.includes(q);
    const matchesCat = !categoryFilter || a.category === categoryFilter;
    return matchesText && matchesCat;
  });

  const categories = [...new Set(articles.map((a) => a.category))].sort();

  const handleSelect = async (article: ArticleListItem) => {
    setLoadingContent(article.filePath);
    try {
      const res = await api.getArticle(token, article.filePath);
      onSelectArticle(res.filePath, res.content, `Edit: ${article.title}`);
      onClose();
    } catch (e) {
      if (e instanceof AuthError) { onAuthError(); return; }
      setError(e instanceof Error ? e.message : 'Failed to load article');
    } finally {
      setLoadingContent(null);
    }
  };

  const inputCls =
    'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setQuery(''); setCategoryFilter(''); onClose(); } }}>
      <DialogContent className="sm:max-w-xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Edit existing article</DialogTitle>
        </DialogHeader>

        {/* min-w-0 prevents this grid item from expanding the dialog beyond max-width */}
        <div className="min-w-0 space-y-3">
          <input
            className={inputCls}
            placeholder="Search by title or slug..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />

          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setCategoryFilter('')}
                className={`rounded-full px-2.5 py-0.5 text-xs border transition-colors ${
                  !categoryFilter ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-accent'
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}
                  className={`rounded-full px-2.5 py-0.5 text-xs border transition-colors ${
                    categoryFilter === cat ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-accent'
                  }`}
                >
                  {CATEGORY_LABELS[cat] ?? cat}
                </button>
              ))}
            </div>
          )}

          <div className="max-h-80 overflow-y-auto rounded-md border">
            {loading && (
              <div className="flex justify-center py-8">
                <Spinner label="Loading articles…" />
              </div>
            )}
            {error && (
              <p className="px-3 py-6 text-sm text-destructive text-center">{error}</p>
            )}
            {!loading && !error && filtered.length === 0 && (
              <p className="px-3 py-6 text-sm text-muted-foreground text-center">
                {articles.length === 0 ? 'No articles found.' : 'No matches.'}
              </p>
            )}
            {!loading && !error && filtered.map((article) => (
              /* block (not w-full) so width is computed from the content area,
                 which naturally excludes the scrollbar in a block formatting context */
              <button
                key={article.filePath}
                type="button"
                disabled={loadingContent !== null}
                onClick={() => handleSelect(article)}
                className="block w-full text-left px-3 py-2.5 hover:bg-accent transition-colors border-b last:border-b-0 disabled:opacity-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{article.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{article.filePath}</div>
                  </div>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {CATEGORY_LABELS[article.category] ?? article.category}
                  </span>
                  {loadingContent === article.filePath && (
                    <Spinner label="Loading…" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
